import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http'
import { Injectable, NgZone } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { Store } from '@ngrx/store'
import { EMPTY, noop, Observable, throwError } from 'rxjs'
import { catchError, filter, tap } from 'rxjs/operators'
import { appConfig } from '../../../brands/_current/config/app-config'
import { PendingData } from '../../pending-operation/shared/pending-data.model'
import { fromSignUpPage } from '../../pending-operation/shared/pending-operation-params'
import { PendingOperationService } from '../../pending-operation/shared/pending-operation.service'
import { WindowRefService } from '../../shared/globals-services/window-ref.service'
import {
  AccountErrorCodeEnum,
  AuthenticationAdditionalActionEnum,
} from '../../shared/mycicero-web-api/shared/base-login-output.model'
import { isBaseOutput } from '../../shared/mycicero-web-api/shared/base-output.model'
import { isRedirectOutput } from '../../shared/mycicero-web-api/shared/redirect-output.model'
import { fromSignUpFinalizationPage } from '../../sign-up-finalization/shared/sign-up-finalization-params'
import { AccountStatus, getAccountStatusFromErrorCode, isAccountStatus } from '../account-status.enum'
import { accountStatusReceived } from '../store/actions'
import { ExternalProviderEnumArray } from '../../login/external-provider/external-provider.enum'
import { REDIRECT_URL_PARAM } from '../../login/shared/login-query-params'

@Injectable()
export class AccountStatusInterceptor implements HttpInterceptor {
  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly zone: NgZone,
    private readonly store: Store,
    private readonly windowRefService: WindowRefService,
    private readonly pendingOperationService: PendingOperationService,
  ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      filter(() => request.url.includes(appConfig.myCiceroWebApi.urls._base)),
      tap((response) => {
        if (response instanceof HttpResponse && (!isBaseOutput(response.body) || !response.body.CodeError)) {
          /**
           * AccountState API property is deprecated and replaced by CodeError property.
           * However, some calls return a success response and so an empty CodeError, but they tacitly confirm that the account is active
           */
          const activeAccountCalls = [
            appConfig.myCiceroWebApi.urls.registerAccount,
            appConfig.myCiceroWebApi.urls.confirmPinSms,
            appConfig.myCiceroWebApi.urls.confirmEmail,
            appConfig.myCiceroWebApi.urls.changeRegistrationCellAndSendSms,
            appConfig.myCiceroWebApi.urls.insertPhone,
            appConfig.myCiceroWebApi.urls.acceptConsents,
            appConfig.myCiceroWebApi.urls.confirmOtpForAccountMatching,
            appConfig.myCiceroWebApi.urls.externalLogin,
            appConfig.myCiceroWebApi.urls.checkEmailCertification,
          ]
          if (
            isBaseOutput(response.body) &&
            response.body.Success &&
            activeAccountCalls.some((url) => response.url.includes(url))
          ) {
            this.store.dispatch(accountStatusReceived({ accountStatus: AccountStatus.active }))
          }

          /**
           * If the user comes from the sign-up flow, then the application redirects him to the sing-up success page,
           * otherwise completes the authentication flow by redirecting to the URL (generally the identity-server URL)
           * passed by the relative response property
           */
          if (isRedirectOutput(response.body)) {
            if (
              response.body.AuthenticationAdditionalAction === AuthenticationAdditionalActionEnum.mooneyRegistration
            ) {
              this.zone
                .run(() =>
                  this.router.navigate(
                    [
                      '/',
                      appConfig.login.paths._root,
                      appConfig.login.externalProvider.paths._root,
                      ExternalProviderEnumArray[2],
                      appConfig.login.externalProvider.paths.success,
                    ],
                    {
                      queryParams: { [REDIRECT_URL_PARAM]: response.body.UrlRedirect },
                      queryParamsHandling: 'merge',
                    },
                  ),
                )
                .then(noop)
            } else if (this.route.snapshot.queryParams[fromSignUpPage]) {
              this.zone
                .run(() =>
                  this.router.navigate([appConfig.signUp.paths._root, appConfig.feedback.paths.success], {
                    queryParams: { [REDIRECT_URL_PARAM]: response.body.UrlRedirect },
                    queryParamsHandling: 'merge',
                  }),
                )
                .then(noop)
            } else if (this.route.snapshot.queryParams[fromSignUpFinalizationPage]) {
              this.zone
                .run(() =>
                  this.router.navigate([appConfig.signUpFinalization.paths._root, appConfig.feedback.paths.success], {
                    queryParams: { [REDIRECT_URL_PARAM]: response.body.UrlRedirect },
                    queryParamsHandling: 'merge',
                  }),
                )
                .then(noop)
            } else if (response.body.UrlRedirect) {
              this.windowRefService.window.location.href = response.body.UrlRedirect
            }
          }
        }
      }),
      catchError((err) => {
        if (err instanceof HttpErrorResponse && isBaseOutput<AccountErrorCodeEnum>(err.error)) {
          const codeError = err.error.CodeError
          const accountStatus = getAccountStatusFromErrorCode(codeError)
          if (isAccountStatus(accountStatus)) {
            const state: PendingData = {
              password: request.body['Password'],
              mobileNumber: request.body['Mobile'],
              mobileCountryCode: request.body['Prefix'],
            }

            this.store.dispatch(accountStatusReceived({ accountStatus }))

            switch (accountStatus) {
              case AccountStatus.blocked:
                this.zone
                  .run(() =>
                    this.router.navigate([appConfig.account.paths._root, appConfig.account.paths.blocked], {
                      queryParamsHandling: 'merge',
                    }),
                  )
                  .then(noop)
                return EMPTY
              case AccountStatus.closing:
                this.zone
                  .run(() =>
                    this.router.navigate([appConfig.account.paths._root, appConfig.account.paths.closing], {
                      queryParamsHandling: 'merge',
                    }),
                  )
                  .then(noop)
                return EMPTY
              case AccountStatus.ambiguous:
                this.pendingOperationService.setPendingData(state)
                this.zone
                  .run(() =>
                    this.router.navigate(
                      [appConfig.pendingOperation.paths._root, appConfig.pendingOperation.paths.emailSelection],
                      {
                        queryParamsHandling: 'merge',
                        state,
                      },
                    ),
                  )
                  .then(noop)
                return EMPTY
            }
          }
        }
        return throwError(() => err)
      }),
    )
  }
}
